import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "Capabilities", id: "capabilities" },
  { label: "Approach", id: "approach" },
  { label: "Industries", id: "industries" },
  { label: "Contact", id: "contact" },
];

export const Nav = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) window.dispatchEvent(new CustomEvent("lenis-scroll-to", { detail: el }));
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="nav-logo-btn">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono-tech text-[11px] tracking-[0.18em] uppercase text-steel hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onQuote}
            data-testid="nav-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.18em] uppercase hover:bg-foreground hover:text-background transition-colors duration-300 group"
          >
            Request Quote
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-foreground"
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-card border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl uppercase font-bold text-foreground"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); onQuote(); }}
                className="bg-accent text-accent-foreground px-5 py-3 font-mono-tech text-xs tracking-[0.18em] uppercase mt-2"
              >
                Request Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
